// frontend/src/components/Events/NewsDetailModal.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  Paper,
  alpha,
  useTheme,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Snackbar,
  GlobalStyles
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { newsAPI } from '../../services/eventsAPI';
import CreateEditNewsModal from './CreateEditNewsModal';

// ============================================
// ГЛОБАЛЬНЫЕ СТИЛИ ДЛЯ АНИМАЦИИ
// ============================================
const animationStyles = {
  '@keyframes heartBeat': {
    '0%': { transform: 'scale(1)' },
    '25%': { transform: 'scale(1.3)' },
    '50%': { transform: 'scale(1)' },
    '75%': { transform: 'scale(1.2)' },
    '100%': { transform: 'scale(1)' }
  }
};

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2),
  },
  '& .MuiDialog-paper': {
    width: 'min(900px, calc(100vw - 32px))',
    maxWidth: 900,
    minWidth: 0,
    maxHeight: 'calc(100vh - 32px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.74)},
        ${alpha(theme.palette.grey[100], 0.46)} 46%,
        ${alpha(theme.palette.primary.main, 0.08)}
      ),
      radial-gradient(circle at 8% 4%, ${alpha(theme.palette.info.light, 0.15)}, transparent 34%),
      radial-gradient(circle at 92% 90%, ${alpha(theme.palette.primary.main, 0.13)}, transparent 42%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.56),
    backdropFilter: 'blur(30px) saturate(150%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
    zIndex: 1300,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.2, 2.8),
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.50)}, ${alpha(theme.palette.common.white, 0.14)})`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: 0,
  background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.16)}, ${alpha(theme.palette.primary.main, 0.025)})`,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.32),
    borderRadius: '999px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.22),
    borderRadius: '999px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.42),
    },
  },
}));

const MediaContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 440,
  backgroundColor: theme.palette.grey[900],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  isolation: 'isolate',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: -28,
    backgroundImage: 'var(--media-background)',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    filter: 'blur(24px) saturate(112%)',
    transform: 'scale(1.08)',
    opacity: 0.72,
    zIndex: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `
      linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.16)),
      radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.18) 100%)
    `,
    pointerEvents: 'none',
    zIndex: 1,
  },
}));

const MediaImage = styled('img')({
  position: 'relative',
  zIndex: 2,
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  filter: 'drop-shadow(0 14px 28px rgba(0,0,0,0.18))',
});

const MediaNavButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 42,
  height: 42,
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.10),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.26)}`,
  backdropFilter: 'blur(14px) saturate(140%)',
  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
  boxShadow: '0 8px 22px rgba(0,0,0,0.12)',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    borderColor: alpha(theme.palette.primary.main, 0.34),
    boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.18)}`,
    transform: 'translateY(-50%) scale(1.06)',
  },
  zIndex: 4,
}));

const MediaCounter = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 14,
  right: 14,
  backgroundColor: alpha(theme.palette.primary.main, 0.20),
  color: theme.palette.primary.dark,
  borderRadius: 999,
  padding: '6px 11px',
  fontSize: '0.78rem',
  fontWeight: 650,
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.30)}`,
  boxShadow: `0 7px 18px ${alpha(theme.palette.primary.main, 0.12)}`,
  backdropFilter: 'blur(14px) saturate(145%)',
  WebkitBackdropFilter: 'blur(14px) saturate(145%)',
  zIndex: 4,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.4, 2.8, 2.2),
}));

const AuthorChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.8, 1.25),
  background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.48)}, ${alpha(theme.palette.common.white, 0.18)})`,
  backgroundColor: alpha(theme.palette.common.white, 0.30),
  border: `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
  backdropFilter: 'blur(14px) saturate(130%)',
  WebkitBackdropFilter: 'blur(14px) saturate(130%)',
  boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.045)}`,
  borderRadius: 999,
  width: 'fit-content',
}));

const StatBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.primary.main
}));

// ============================================
// 🔥 НАТИВНЫЙ ВИДЕО-ПЛЕЕР
// ============================================

const NativeVideoPlayer = ({ url }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !url) return;

    containerRef.current.innerHTML = '';

    const video = document.createElement('video');
    video.controls = true;
    video.src = url;
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    video.style.backgroundColor = '#000';
    video.setAttribute('playsinline', '');
    video.setAttribute('controlsList', 'nodownload');

    containerRef.current.appendChild(video);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [url]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 400,
        bgcolor: '#000',
        position: 'relative'
      }}
    />
  );
};

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const NewsDetailModal = ({
  open,
  onClose,
  news,
  onDelete,
  canEdit = false,
  canDelete = false,
  isLiked = false,           // 🔥 НОВЫЙ ПРОПС
  onLikeToggle               // 🔥 НОВЫЙ ПРОПС
}) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [likesCount, setLikesCount] = useState(news?.likes_count || 0);
  const [viewsCount, setViewsCount] = useState(news?.views_count || 0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const mediaItems = news?.media || [];
  const currentMedia = mediaItems[currentMediaIndex];

  // Синхронизируем счетчик при изменении пропса
  useEffect(() => {
    setLikesCount(news?.likes_count || 0);
    setViewsCount(news?.views_count || 0);
  }, [news]);

  // Сброс индекса при смене новости
  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [news]);

  const handlePreviousMedia = () => {
    setCurrentMediaIndex((prev) =>
      prev === 0 ? mediaItems.length - 1 : prev - 1
    );
  };

  const handleNextMedia = () => {
    setCurrentMediaIndex((prev) =>
      prev === mediaItems.length - 1 ? 0 : prev + 1
    );
  };

  // 🔥 ОБРАБОТЧИК ЛАЙКА
  const handleLike = async (e) => {
    e.stopPropagation();

    try {
      // Оптимистичное обновление счетчика
      if (isLiked) {
        setLikesCount(prev => prev - 1);
      } else {
        setLikesCount(prev => prev + 1);
      }

      // Вызываем родительскую функцию
      await onLikeToggle(news.id, e);

    } catch (err) {
      console.error('Ошибка при лайке:', err);
      // Откат при ошибке
      setLikesCount(news?.likes_count || 0);
      setSnackbar({
        open: true,
        message: 'Не удалось поставить лайк',
        severity: 'error'
      });
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setSnackbar({
      open: true,
      message: 'Ссылка скопирована в буфер обмена',
      severity: 'success'
    });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    setEditModalOpen(true);
  };

  const handleDelete = async () => {
    handleMenuClose();
    if (!window.confirm('Вы уверены, что хотите удалить эту новость?')) return;

    setLoading(true);
    try {
      await newsAPI.deleteNews(news.id);
      setSnackbar({
        open: true,
        message: 'Новость успешно удалена',
        severity: 'success'
      });
      setTimeout(() => {
        onClose(true);
        if (onDelete) onDelete();
      }, 1500);
    } catch (err) {
      console.error('Ошибка при удалении:', err);
      setSnackbar({
        open: true,
        message: 'Не удалось удалить новость',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = (updatedNews) => {
    setEditModalOpen(false);
    onClose(true);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!news) return null;

  return (
    <>
      <GlobalStyles styles={animationStyles} />

      <StyledDialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
        <StyledDialogTitle>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              component="span"
              sx={{
                display: 'block',
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.15rem' },
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {news.title}
            </Typography>
            {news.section && (
              <Chip
                size="small"
                label={news.section_title}
                sx={{
                  mt: 0.65,
                  height: 24,
                  borderRadius: 999,
                  fontSize: '0.68rem',
                  fontWeight: 650,
                  color: news.section_color || 'primary.main',
                  bgcolor: alpha(news.section_color || theme.palette.primary.main, 0.09),
                  border: `1px solid ${alpha(news.section_color || theme.palette.primary.main, 0.15)}`,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(canEdit || canDelete) && (
              <>
                <IconButton
                  onClick={handleMenuOpen}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: alpha(theme.palette.primary.main, 0.09),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.16),
                      boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.14)}`,
                    },
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {canEdit && (
                    <MenuItem onClick={handleEdit}>
                      <ListItemIcon>
                        <EditIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Редактировать</ListItemText>
                    </MenuItem>
                  )}
                  {canDelete && (
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText>Удалить</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
            <IconButton
              onClick={() => onClose(false)}
              size="small"
              sx={{
                width: 36,
                height: 36,
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.10),
                border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
                '&:hover': {
                  color: 'primary.dark',
                  backgroundColor: alpha(theme.palette.primary.main, 0.18),
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.16)}`,
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </StyledDialogTitle>

        <StyledDialogContent>
          {/* Медиа-карусель */}
          {mediaItems.length > 0 && (
            <MediaContainer
              sx={{
                '--media-background': currentMedia.type === 'image'
                  ? `url("${currentMedia.url}")`
                  : 'none',
              }}
            >
              {currentMedia.type === 'video' ? (
                <NativeVideoPlayer url={currentMedia.url} />
              ) : (
                <MediaImage src={currentMedia.url} alt={`media-${currentMediaIndex}`} />
              )}

              {mediaItems.length > 1 && (
                <>
                  <MediaNavButton
                    onClick={handlePreviousMedia}
                    sx={{ left: 16 }}
                    size="small"
                  >
                    <ArrowBackIcon />
                  </MediaNavButton>
                  <MediaNavButton
                    onClick={handleNextMedia}
                    sx={{ right: 16 }}
                    size="small"
                  >
                    <ArrowForwardIcon />
                  </MediaNavButton>
                  <MediaCounter>
                    {currentMedia.type === 'video' ? <VideoIcon /> : <ImageIcon />}
                    {currentMediaIndex + 1} / {mediaItems.length}
                  </MediaCounter>
                </>
              )}
            </MediaContainer>
          )}

          <ContentSection>
            {/* Информация об авторе и дате */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              mb: 2.4,
              flexWrap: 'wrap',
            }}>
              <AuthorChip>
                <Avatar src={news.created_by?.avatar} sx={{ width: 24, height: 24 }}>
                  {news.created_by?.full_name?.charAt(0) || <PersonIcon />}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="500">
                    {news.created_by?.full_name || news.created_by?.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(news.created_at)}
                  </Typography>
                </Box>
              </AuthorChip>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <StatBadge>
                  <VisibilityIcon fontSize="small" />
                  <Typography variant="body2">{viewsCount}</Typography>
                </StatBadge>

                {/* 🔥 ЛАЙК С АНИМАЦИЕЙ */}
                <StatBadge>
                  <IconButton
                    size="small"
                    onClick={handleLike}
                    sx={{
                      p: 0,
                      transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
                      '&:hover': {
                        transform: 'scale(1.2)'
                      },
                      '&:active': {
                        transform: 'scale(1.5)'
                      }
                    }}
                  >
                    {isLiked ? (
                      <FavoriteIcon
                        sx={{
                          color: 'error.main',
                          fontSize: 20,
                          filter: 'drop-shadow(0 0 4px rgba(196, 69, 69, 0.5))',
                          animation: 'heartBeat 0.3s ease-in-out'
                        }}
                      />
                    ) : (
                      <FavoriteBorderIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    )}
                  </IconButton>
                  <Typography variant="body2">{likesCount}</Typography>
                </StatBadge>

                <IconButton
                  size="small"
                  onClick={handleShare}
                  sx={{
                    width: 34,
                    height: 34,
                    backgroundColor: alpha(theme.palette.common.white, 0.42),
                    border: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
                    '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.68) },
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Контент новости */}
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.7,
                color: 'text.primary'
              }}
            >
              {news.content}
            </Typography>

            {/* Мета-информация */}
            {news.section && (
              <Box sx={{ mt: 3 }}>
                <Chip
                  label={`Секция: ${news.section_title}`}
                  size="small"
                  sx={{
                    bgcolor: alpha(news.section_color || theme.palette.primary.main, 0.1),
                    color: news.section_color || theme.palette.primary.main
                  }}
                />
              </Box>
            )}
          </ContentSection>
        </StyledDialogContent>

        <DialogActions
        sx={{
          p: { xs: 1.6, sm: 2 },
          pt: 1.1,
          flexShrink: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.34)}, ${alpha(theme.palette.common.white, 0.12)})`,
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.58)}`,
        }}
      >
        <Button
          onClick={() => onClose(false)}
          variant="outlined"
          sx={{
            borderRadius: 999,
            px: 2.5,
            textTransform: 'none',
            fontWeight: 650,
            borderColor: alpha(theme.palette.primary.main, 0.28),
            backgroundColor: alpha(theme.palette.primary.main, 0.09),
            color: 'primary.main',
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.42),
              backgroundColor: alpha(theme.palette.primary.main, 0.16),
              boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.14)}`,
            },
          }}
        >
          Закрыть
        </Button>
      </DialogActions>
      </StyledDialog>

      {/* Модалка редактирования */}
      {editModalOpen && (
        <CreateEditNewsModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          eventId={news.event}
          sections={[]}
          newsToEdit={news}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Снэкбар */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NewsDetailModal;